import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const useActivityEnrollment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Enroll a student in an activity
  const enrollStudent = useMutation({
    mutationFn: async ({ 
      studentId, 
      activityId 
    }: { 
      studentId: string; 
      activityId: string 
    }) => {
      
      
      try {
        const { error } = await supabase
          .from('student_activities')
          .insert({
            student_id: studentId,
            activity_id: activityId,
            status: 'active'
          });
  
        // If already exists, reactivate the enrollment
        if (error && (error.code === '23505' || error.code === '409')) {
          const { error: updateError } = await supabase
            .from('student_activities')
            .update({ status: 'active' })
            .eq('student_id', studentId)
            .eq('activity_id', activityId);
            
          if (updateError) throw updateError;
        } else if (error) {
          throw error;
        }
        
        return { success: true };
      } catch (error: any) {
        
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate queries that depend on this data
      queryClient.invalidateQueries({ queryKey: ['students-list'] });
      queryClient.invalidateQueries({ queryKey: ['activities-list'] });
      queryClient.invalidateQueries({ queryKey: ['student-details'] });
      
      toast({
        title: "Estudiante inscrito",
        description: "El estudiante ha sido inscrito exitosamente en la actividad."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "No se pudo inscribir al estudiante: " + error.message,
        variant: "destructive"
      });
    }
  });
  
  // Unenroll a student from an activity
  const unenrollStudent = useMutation({
    mutationFn: async ({ 
      studentId, 
      activityId 
    }: { 
      studentId: string; 
      activityId: string 
    }) => {
      
      
      const { error } = await supabase
        .from('student_activities')
        .update({ status: 'inactive' })
        .eq('student_id', studentId)
        .eq('activity_id', activityId);

      if (error) {
        
        throw error;
      }
      
      return { success: true };
    },
    onSuccess: () => {
      // Invalidate queries that depend on this data
      queryClient.invalidateQueries({ queryKey: ['students-list'] });
      queryClient.invalidateQueries({ queryKey: ['activities-list'] });
      queryClient.invalidateQueries({ queryKey: ['student-details'] });
      
      toast({
        title: "Estudiante desinscrito",
        description: "El estudiante ha sido removido de la actividad."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "No se pudo desinscribir al estudiante: " + error.message,
        variant: "destructive"
      });
    }
  });
  
  return {
    enrollStudent,
    unenrollStudent
  };
};